import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { AppBar, Button, Container, Toolbar, Typography, Card, CardHeader, CardContent } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import { isEqual } from 'lodash'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { reloadFacilities } from '../../../actions/facilities/facilities.actions'
import { showError } from '../../../actions/misc/misc.actions'
import StyledPreviewText from '../../../components/Common/StyledPreviewText'
import DaysOrHoursBeforeEntry from '../../../components/Common/DaysOrHoursBeforeEntry'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { DEFAULT_FACILITY_CONTACT_PHONE, TRAINING } from '../../../utils'

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: '1.5rem',
		paddingLeft: '1.5rem',
		paddingRight: '1.5rem',
		paddingBottom: '1.5rem'
	},
	card: { maxWidth: '945px' },
	cardContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: '1rem'
	},
	mainHeader: { fontWeight: 800, marginBottom: 16 },
	header: {
		fontSize: '.75rem',
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8,
		textTransform: 'uppercase'
	},
	previewTextContainer: {
		width: '25rem'
	},
	appBar: {
		top: 'auto',
		bottom: 0,
		zIndex: 10000
	},
	appBarButtonsContainer: {
		[theme.breakpoints.up('md')]: {
			marginLeft: '15rem'
		}
	}
	// gridDouble: {
	// 	display: 'flex',
	// 	flexDirection: 'row',
	// 	justifyContent: 'flex-start',
	// 	alignItems: 'flex-start',
	// 	gap: '1rem',
	// 	'& > div': {
	// 		flex: 1
	// 	}
	// }
}))

const TrainingBooking = () => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const { facility, loading, trainingServicesSetup, updateFacilityServicesByServiceLine } = useSelectedFacility()

	// making this an object so it will force a re-render in the DaysOrHoursBeforeEntry component when user hits Cancel and initialize is called again.
	const [initialCutoffValue, setInitialCutoffValue] = useState({ hours: 0 })

	const [updates, setUpdates] = useState({})

	const initialize = useCallback(() => {
		if (trainingServicesSetup) {
			const { booking_cutoff_hours } = trainingServicesSetup
			const hoursToInt = parseInt(booking_cutoff_hours, 10)
			setInitialCutoffValue({ hours: hoursToInt })
			setUpdates({ ...trainingServicesSetup })
		}
	}, [trainingServicesSetup, setInitialCutoffValue, setUpdates])

	useEffect(() => {
		if (!facility) {
			dispatch(reloadFacilities())
		}
	}, [facility])

	useEffect(() => {
		initialize()
	}, [initialize])

	const saveSelectedFacility = useCallback(async () => {
		try {
			await updateFacilityServicesByServiceLine({
				service_line: TRAINING,
				updates,
				facility_id: facility._id
			})
		} catch (error) {
			dispatch(showError(error || 'An error occured while updating the selected Facility.'))
		}
	}, [dispatch, facility, updates, updateFacilityServicesByServiceLine])

	const clearChanges = useCallback(() => {
		initialize()
	}, [initialize])

	const handleDaysOrHoursBeforeEntry = useCallback(
		({ hours }) => {
			setUpdates((prev) => ({ ...prev, booking_cutoff_hours: hours }))
		},
		[setUpdates]
	)

	const enableSave = useMemo(() => {
		if (!trainingServicesSetup) return false
		let dirty = !isEqual(trainingServicesSetup, updates)
		return dirty
	}, [trainingServicesSetup, updates])

	const previewText = useMemo(() => {
		const { booking_cutoff_hours } = updates
		let timeToDisplay = ''
		if (booking_cutoff_hours > 48 && booking_cutoff_hours % 24 === 0) {
			timeToDisplay = `${booking_cutoff_hours / 24} days`
		} else {
			timeToDisplay = `${booking_cutoff_hours} hours`
		}
		return `The window for last-minute bookings is ${timeToDisplay} before the training course begins. If you need to enroll within that time frame, please text us at ${
			facility?.contact?.phone_number || DEFAULT_FACILITY_CONTACT_PHONE
		} and we'll let you know if we can accommodate your request.`
	}, [facility, updates])

	return (
		<div>
			<Container className={classes.root} maxWidth="auto">
				<Typography variant="h5" className={classes.mainHeader}>
					Booking
				</Typography>
				<Card className={classes.card}>
					<CardHeader title="Training Booking Settings" />
					<CardContent>
						<div className={classes.cardContentContainer}>
							<DaysOrHoursBeforeEntry
								initialHours={initialCutoffValue}
								onUpdated={handleDaysOrHoursBeforeEntry}
							/>
							<div>
								<Typography className={classes.header}>Preview Cut-off Text</Typography>
								<div className={classes.previewTextContainer}>
									<StyledPreviewText text={previewText} />
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				{enableSave ? (
					<AppBar position="fixed" color="primary" className={classes.appBar}>
						<Toolbar>
							<div className={classes.appBarButtonsContainer}>
								<Button
									variant="contained"
									color="secondary"
									disabled={loading}
									style={{ marginRight: '8px' }}
									onClick={() => saveSelectedFacility()}>
									Save Changes
								</Button>
								<Button variant="outlined" color="secondary" onClick={clearChanges}>
									Cancel
								</Button>
							</div>
						</Toolbar>
					</AppBar>
				) : null}
			</Container>
			{loading ? <LoadingOverlay /> : null}
		</div>
	)
}

export default TrainingBooking
